import React, { useEffect, useState } from "react";
import { X, Plus, Minus, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * Click-to-enlarge image lightbox.
 * Renders a full-screen overlay with zoom in / out / reset / close controls.
 *
 * Props:
 *   - src: image URL (string) — required
 *   - alt: accessible label
 *   - open: boolean — controlled visibility
 *   - onClose: () => void
 *   - title: optional caption shown top-left
 *   - testid: optional data-testid prefix
 *   - meta: optional { timestamp, lat, lng, mapTitle } — renders the small
 *           Static Map thumbnail + capture metadata below the image, clickable
 *           to open the full Google Maps page in a new tab.
 */
export default function ImageLightbox({ src, alt, open, onClose, title, testid = "image-lightbox", meta }) {
  const [zoom, setZoom] = useState(1);

  // Reset zoom every time we open
  useEffect(() => {
    if (open) setZoom(1);
  }, [open, src]);

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(z + 0.25, 4));
      else if (e.key === "-") setZoom((z) => Math.max(z - 0.25, 0.5));
      else if (e.key === "0") setZoom(1);
    };
    window.addEventListener("keydown", onKey);
    // Prevent background scroll
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid={testid}
    >
      {/* Top bar */}
      <div
        className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 sm:px-6 py-3 text-white pointer-events-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-sm font-medium truncate pointer-events-auto" data-testid={`${testid}-title`}>{title}</div>
        <div className="flex items-center gap-1.5 pointer-events-auto">
          <button
            type="button"
            onClick={() => setZoom((z) => Math.max(z - 0.25, 0.5))}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
            aria-label="Zoom out"
            data-testid={`${testid}-zoom-out`}
          >
            <Minus size={16} weight="bold" />
          </button>
          <span className="text-xs font-mono w-12 text-center" data-testid={`${testid}-zoom-level`}>{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            onClick={() => setZoom((z) => Math.min(z + 0.25, 4))}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
            aria-label="Zoom in"
            data-testid={`${testid}-zoom-in`}
          >
            <Plus size={16} weight="bold" />
          </button>
          <button
            type="button"
            onClick={() => setZoom(1)}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
            aria-label="Reset zoom"
            data-testid={`${testid}-zoom-reset`}
          >
            <ArrowsClockwise size={16} weight="bold" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center ml-2"
            aria-label="Close"
            data-testid={`${testid}-close`}
          >
            <X size={18} weight="bold" />
          </button>
        </div>
      </div>

      {/* Image */}
      <div
        className="max-w-full max-h-full overflow-auto flex flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
        data-testid={`${testid}-image-wrap`}
      >
        <img
          src={src}
          alt={alt}
          style={{ transform: `scale(${zoom})`, transformOrigin: "center center", transition: "transform 0.15s ease" }}
          className="max-w-[90vw] max-h-[70vh] object-contain rounded-md shadow-2xl select-none"
          draggable={false}
          data-testid={`${testid}-img`}
        />
        {meta && (meta.timestamp || (meta.lat != null && meta.lng != null)) && (
          <div className="bg-white/10 backdrop-blur-md text-white rounded-md px-4 py-3 max-w-[90vw] flex items-center gap-4" data-testid={`${testid}-meta`}>
            <div className="text-xs space-y-0.5 min-w-0">
              {meta.timestamp && (
                <div>
                  <span className="text-white/60 uppercase tracking-wider text-[10px]">Taken:</span>{" "}
                  <span className="font-mono" data-testid={`${testid}-meta-time`}>
                    {new Date(meta.timestamp).toLocaleString()}
                  </span>
                </div>
              )}
              {meta.lat != null && meta.lng != null && (
                <div>
                  <span className="text-white/60 uppercase tracking-wider text-[10px]">GPS:</span>{" "}
                  <span className="font-mono">{Number(meta.lat).toFixed(5)}, {Number(meta.lng).toFixed(5)}</span>
                </div>
              )}
            </div>
            {meta.lat != null && meta.lng != null && (() => {
              const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
              const center = `${meta.lat},${meta.lng}`;
              const staticUrl = apiKey
                ? `https://maps.googleapis.com/maps/api/staticmap?center=${center}&zoom=15&size=180x110&maptype=roadmap&markers=color:red|${center}&key=${apiKey}`
                : null;
              const fullUrl = `https://www.google.com/maps?q=${center}&z=17`;
              return (
                <a
                  href={fullUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 border border-white/20 rounded overflow-hidden hover:border-white/60 transition-colors"
                  title="Open in Google Maps"
                  data-testid={`${testid}-map-link`}
                >
                  {staticUrl ? (
                    <img src={staticUrl} alt="Capture location" width={180} height={110} className="block" />
                  ) : (
                    <div className="w-[180px] h-[110px] flex items-center justify-center bg-zinc-800 text-[10px] text-white/60 px-2 text-center">
                      Open in Google Maps →
                    </div>
                  )}
                </a>
              );
            })()}
          </div>
        )}
      </div>

      <div
        className="absolute bottom-3 left-1/2 -translate-x-1/2 text-[11px] text-white/60 pointer-events-none"
        onClick={(e) => e.stopPropagation()}
      >
        Click outside, press <kbd className="font-mono px-1">Esc</kbd>, or use +/− to zoom
      </div>
    </div>
  );
}
